import React, { useState, FunctionComponent, useEffect } from 'react';
import { faArchive, faEnvelopeOpen, faHandPointLeft } from '@fortawesome/pro-regular-svg-icons';
import { Switch, Redirect, useRouteMatch, Route } from 'react-router-dom';
import { Card, Tabs, Button, useFlashMessage } from '@indigo-ag/component-module';
import { AgConversation } from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';
import i18next from 'i18next';

import { createStylesHook } from 'shared';
import { ConversationItem } from './ConversationItem/ConversationItem';
import { BlankState } from './BlankState/BlankState';
import { archiveConversation } from 'features/brazil-grower/queries';
import { ChatBox } from './ChatBox/ChatBox';
import { history } from 'store';

interface DocumentRequestsListProps {
  conversations: AgConversation[];
}

const DEFAULT_PANE_MEASURE = {
  height: '65vh',
  width: '50%'
};

const useStyles = createStylesHook({
  layout: {
    width: '100%',
    height: DEFAULT_PANE_MEASURE.height,
    minHeight: 500,
    display: 'flex',
    padding: 10,
    maxHeight: 900
  },
  leftPane: {
    minWidth: DEFAULT_PANE_MEASURE.width,
    height: '100%',
    display: 'flex',
    width: '100%',
    maxWidth: 650
  },
  rightPane: {
    minWidth: DEFAULT_PANE_MEASURE.width,
    minHeight: 450
  },
  conversationList: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 18px',
    position: 'relative',
    width: '100%'
  },
  tabsLayout: {
    display: 'flex !important',
    flexDirection: 'column',
    width: '100%',
    height: '100%'
  },
  tabs: {
    borderBottom: 'none !important',
    paddingBottom: 5,
    '& button[role="tab"]': {
      marginLeft: '6px',
      marginRight: '24px',
      padding: '16px 0'
    }
  },
  tabPane: {
    paddingTop: 20,
    overflowY: 'auto',
    width: '100%',
    height: '100%'
  },
  noConversations: {
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: 'lighter',
    fontStyle: 'italic',
    marginTop: '32px'
  }
});

const TABS_CONFIG = {
  all: i18next.t('all', { context: 'female' }),
  archived: i18next.t('archived')
};

export const DEFAULT_TAB_ID = 'all';

export const Conversations: FunctionComponent<DocumentRequestsListProps> = ({ conversations }) => {
  /**
   * State
   */
  const [openNewConversation, setOpenNewConversation] = useState(false);

  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * Routes Marching
   */
  const {
    params: { tabId: urlParamTabId, conversationId: urlParamConversationId },
    url: currentUrl
  } = useRouteMatch<{ tabId: string; conversationId: string }>();

  const BASE_URL = currentUrl.replace(/(?=conversations)\S+/gm, 'conversations');

  /**
   * State
   */
  const [activeTabId, setActiveTab] = useState(urlParamTabId);
  const [activeConversation, setActiveConversation] = useState<AgConversation>();
  const [archivingConversationId, setArchivingConversationId] = useState<AgConversation['id']>();
  const [isArchivingConversation, setIsArchivingConversation] = useState(false);

  /**
   * Mutations
   */
  const [archiveConversationFn] = useMutation(archiveConversation);

  const { addFlashMessage } = useFlashMessage();

  const onArchiveConversation = ({ id: conversationId, archived }: Partial<AgConversation>) => {
    setIsArchivingConversation(true);
    setArchivingConversationId(conversationId);
    setActiveConversation(undefined);
    archiveConversationFn({
      awaitRefetchQueries: true,
      variables: {
        conversationId,
        archived
      },
      refetchQueries: ['financingAdminNotifications']
    }).then(() => {
      setIsArchivingConversation(false);
      addFlashMessage({
        content: i18next.t('hasBeenUpdatedSuccessfully'),
        title: '',
        variant: 'success'
      });
    });
  };

  const renderConversations = (tabId: string) => {
    const isAllTab = tabId === DEFAULT_TAB_ID;
    const filteredConversations = conversations
      .filter(({ archived }) => (isAllTab ? !archived : archived))
      .map(conversation => (
        <ConversationItem
          key={conversation.id}
          conversationInfo={conversation}
          isActive={activeConversation?.id === conversation.id}
          onClickConversation={handleConversationSelect(conversation)}
          onArchive={onArchiveConversation}
          isArchivingConversation={archivingConversationId === conversation.id && isArchivingConversation}
        />
      ));

    if (filteredConversations.length) {
      return filteredConversations;
    }

    return isAllTab ? (
      <BlankState
        title={i18next.t('conversationBlankStateTitle')}
        description={i18next.t('conversationBlankStateDescription')}
        icon={faEnvelopeOpen}
      />
    ) : (
      <BlankState
        description={i18next.t('conversationArchiveBSDescription')}
        icon={faArchive}
        title={i18next.t('conversationArchiveBSTitle')}
        height="10rem"
      />
    );
  };

  /**
   * Callbacks
   */
  useEffect(() => {
    if (urlParamTabId !== activeTabId) {
      setActiveTab(urlParamTabId);
      setActiveConversation(undefined);
    }

    if (urlParamTabId && urlParamConversationId !== activeConversation?.id) {
      setActiveConversation(conversations.find(({ id }) => id === urlParamConversationId));
      setOpenNewConversation(false);
    }
    // eslint-disable-next-line
  }, [activeTabId, history.location.pathname]);

  const handleOpenNewConversation = () => {
    setOpenNewConversation(true);
    setActiveConversation(undefined);
  };

  const handleCreateNewConversation = (conversation: AgConversation) => {
    history.push(`${BASE_URL}/${DEFAULT_TAB_ID}`);
    setOpenNewConversation(false);
    setActiveConversation(conversation);
  };

  const handleChangeTab = (selectedTab: string) => {
    history.push(`${BASE_URL}/${selectedTab}`);
  };

  const handleConversationSelect = (conversation: AgConversation) => {
    return () => history.push(`${BASE_URL}/${activeTabId}/${conversation.id}`);
  };

  const getRedirectUrl = (): string => {
    if (!urlParamTabId && !urlParamConversationId) {
      return `${BASE_URL}/${DEFAULT_TAB_ID}`;
    }
    return `${currentUrl}`;
  };

  return conversations.length || openNewConversation ? (
    <>
      <Card className={classes.layout}>
        <div className={classes.leftPane}>
          <div className={classes.conversationList}>
            <div className={classes.tabsLayout}>
              <Tabs
                className={classes.tabs}
                activeTabId={activeTabId}
                ariaLabel="conversation tabs"
                items={Object.entries(TABS_CONFIG).map(([id, title]) => ({ id, title }))}
                onChange={handleChangeTab}
              />
              <div className={classes.tabPane}>
                <Switch>
                  <Redirect exact from={BASE_URL} to={getRedirectUrl()} />
                  <Route path={`${BASE_URL}/${activeTabId}`}>{renderConversations(activeTabId)}</Route>
                </Switch>
              </div>
            </div>
            <Button
              style={{ position: 'absolute', right: 40 }}
              onClick={handleOpenNewConversation}
              variant="secondary"
              disabled={openNewConversation}
              size="small"
            >
              {i18next.t('newConversation')}
            </Button>
          </div>
        </div>
        <div className={classes.rightPane}>
          {activeConversation || openNewConversation ? (
            <ChatBox
              key={activeConversation?.id || 1}
              openNewConversation={openNewConversation}
              onCreateNewConversation={handleCreateNewConversation}
              selectedConversation={activeConversation}
              isArchiveView={activeTabId === 'archived'}
              onArchive={onArchiveConversation}
            />
          ) : (
            <BlankState
              title={i18next.t('conversationBlankSelectTitle')}
              description={i18next.t('conversationBlankSelectDescription')}
              icon={faHandPointLeft}
            />
          )}
        </div>
      </Card>
    </>
  ) : (
    <BlankState
      onClickButton={handleOpenNewConversation}
      title={i18next.t('conversationBlankStateTitle')}
      description={i18next.t('conversationBlankStateDescription')}
      icon={faEnvelopeOpen}
    />
  );
};
